import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Aviso, Botao, useDialogo } from '../../componentes/ui';
import { useIdentidade } from '../../hooks/useSessao';
import {
  CHAVE_DA_BECA,
  buscarConversa,
  buscarConversas,
  buscarSituacao,
  excluirConversa,
  perguntarABeca,
  type ConversaResumo,
  type MensagemDaBeca,
  type SituacaoDaBeca,
} from '../../servicos/beca';
import { Pagina } from '../Carteira';
import { ErroDaApi } from '../ErroDaApi';
import { AcoesDaResposta } from './AcoesDaResposta';
import { TextoDaBeca } from './TextoDaBeca';

/** Para começar sem página em branco — o tipo de pergunta que a BECA responde bem. */
const SUGESTOES = [
  'Quem está com parcela atrasada?',
  'Mande uma cobrança para quem está com parcela atrasada há mais de 15 dias',
  'Quais negociações do Pontual estão aguardando contrato assinado?',
  'Como está o quadro Comercial do CRM?',
  'Quais empresas do SISCRED têm mais crédito e ainda não estão no CRM?',
  'Crie uma automação: quando um Pontual for concluído, comentar o valor no card',
] as const;

const CHAVE_SITUACAO = [...CHAVE_DA_BECA, 'situacao'] as const;
const CHAVE_CONVERSAS = [...CHAVE_DA_BECA, 'conversas'] as const;
const chaveDaConversa = (id: string | null) => [...CHAVE_DA_BECA, 'conversa', id] as const;

/**
 * BECA IA (D-013) — a assessora de IA do admin, abaixo do Painel no menu.
 *
 * Nesta fase ela consulta o Deca e responde; agir (mover card, registrar
 * pagamento, mandar e-mail) chega na próxima, com confirmação de quem pediu.
 * A conversa aberta fica no endereço (`?conversa=`): recarregar não a perde.
 */
export function Beca(): JSX.Element {
  const identidade = useIdentidade();
  // A rota é do admin; quem não é volta ao painel em vez de ver uma tela que a API recusaria.
  if (identidade !== null && !identidade.isAdmin) return <Navigate to="/painel" replace />;
  return <ConversaComABeca />;
}

function ConversaComABeca(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const conversaId = params.get('conversa');
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const [texto, setTexto] = useState('');
  /** A pergunta que está sendo respondida — já aparece na conversa antes da resposta. */
  const [pendente, setPendente] = useState<string | null>(null);
  const [listaAberta, setListaAberta] = useState(false);
  const rolagem = useRef<HTMLDivElement>(null);

  const situacao = useQuery({ queryKey: CHAVE_SITUACAO, queryFn: buscarSituacao });
  const conversas = useQuery({ queryKey: CHAVE_CONVERSAS, queryFn: buscarConversas });
  const conversa = useQuery({
    queryKey: chaveDaConversa(conversaId),
    queryFn: () => buscarConversa(conversaId ?? ''),
    enabled: conversaId !== null,
  });

  const abrir = (id: string | null): void => {
    setParams(id === null ? {} : { conversa: id });
    setListaAberta(false);
  };

  const perguntar = useMutation({
    mutationFn: perguntarABeca,
    onMutate: (entrada) => {
      setPendente(entrada.texto);
      setTexto('');
    },
    onSuccess: (r) => {
      queryClient.setQueryData(chaveDaConversa(r.id), r);
      if (r.id !== conversaId) abrir(r.id);
      void queryClient.invalidateQueries({ queryKey: CHAVE_CONVERSAS });
      void queryClient.invalidateQueries({ queryKey: CHAVE_SITUACAO });
    },
    // Não respondeu: a pergunta volta para a caixa, pronta para tentar de novo.
    onError: (_erro, entrada) => setTexto(entrada.texto),
    onSettled: () => setPendente(null),
  });

  const excluir = useMutation({
    mutationFn: excluirConversa,
    onSuccess: (_r, id) => {
      queryClient.removeQueries({ queryKey: chaveDaConversa(id) });
      if (id === conversaId) abrir(null);
      void queryClient.invalidateQueries({ queryKey: CHAVE_CONVERSAS });
    },
  });

  const ligada = situacao.data?.ligada === true;
  const mensagens: readonly MensagemDaBeca[] =
    conversaId === null ? [] : (conversa.data?.mensagens ?? []);

  // A conversa rola sozinha até a última fala.
  useEffect(() => {
    const el = rolagem.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [mensagens.length, pendente, conversaId]);

  const enviar = (pergunta: string): void => {
    const limpa = pergunta.trim();
    if (limpa === '' || perguntar.isPending || !ligada) return;
    perguntar.reset();
    perguntar.mutate({ texto: limpa, ...(conversaId === null ? {} : { conversaId }) });
  };

  const aoEnviar = (e: FormEvent): void => {
    e.preventDefault();
    enviar(texto);
  };

  // Enter envia; Shift+Enter quebra a linha.
  const aoTeclar = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      enviar(texto);
    }
  };

  const pedirExclusao = async (c: ConversaResumo): Promise<void> => {
    const ok = await dialogo.confirmar({
      titulo: 'Apagar esta conversa?',
      descricao: `"${c.titulo}" sai da sua lista. As consultas que a BECA fez não mudaram nada no Deca.`,
      rotuloConfirmar: 'Apagar conversa',
      rotuloCancelar: 'Voltar',
      tom: 'perigo',
    });
    if (ok) excluir.mutate(c.id);
  };

  const vazia = conversaId === null || (conversa.isSuccess && mensagens.length === 0);

  return (
    <Pagina
      titulo="BECA IA"
      descricao="Sua assessora no Deca. Pergunte sobre clientes, negociações, contratos, parcelas, CRM e prospecção: ela consulta o sistema e responde."
      acao={<UsoDoMes situacao={situacao.data} />}
    >
      {situacao.data !== undefined && !situacao.data.ligada && (
        <Aviso tom="atencao" titulo="A BECA ainda não está ligada">
          <p>
            Falta a chave da Anthropic, a empresa da IA que a BECA usa. O TI cria a chave em
            console.anthropic.com, com um limite de gasto mensal, e a configura no Deca como{' '}
            <code className="font-mono text-xs">ANTHROPIC_API_KEY</code>. Assim que ela estiver lá,
            esta tela passa a responder.
          </p>
        </Aviso>
      )}
      {situacao.isError && (
        <ErroDaApi titulo="Não foi possível falar com a BECA" erro={situacao.error} />
      )}

      <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
        {/* ── Conversas ── */}
        <aside className="flex flex-col gap-3">
          <Botao
            variante="secundario"
            larguraTotal
            onClick={() => abrir(null)}
            disabled={perguntar.isPending}
            iconeInicial={<IconeMais />}
          >
            Nova conversa
          </Botao>
          <button
            type="button"
            className="text-left text-xs font-semibold uppercase tracking-wide text-conteudo-sutil lg:hidden"
            aria-expanded={listaAberta}
            onClick={() => setListaAberta((a) => !a)}
          >
            Conversas anteriores ({conversas.data?.length ?? 0}) {listaAberta ? '▴' : '▾'}
          </button>
          <nav
            aria-label="Conversas anteriores"
            className={`${listaAberta ? 'flex' : 'hidden'} max-h-[60vh] flex-col gap-1 overflow-y-auto lg:flex`}
          >
            <p className="hidden px-2 text-xs font-semibold uppercase tracking-wide text-conteudo-sutil lg:block">
              Conversas
            </p>
            {conversas.data?.length === 0 && (
              <p className="px-2 text-sm text-conteudo-sutil">As conversas ficam guardadas aqui.</p>
            )}
            {conversas.data?.map((c) => (
              <div
                key={c.id}
                className={`group flex items-center gap-1 rounded-card ${
                  c.id === conversaId ? 'bg-superficie-vidro' : 'hover:bg-superficie-vidro/60'
                }`}
              >
                <button
                  type="button"
                  onClick={() => abrir(c.id)}
                  aria-current={c.id === conversaId ? 'page' : undefined}
                  className="flex min-w-0 flex-1 flex-col px-2 py-2 text-left"
                >
                  <span className="truncate text-sm text-conteudo-primario">{c.titulo}</span>
                  <span className="text-xs text-conteudo-sutil">{quando(c.atualizadaEm)}</span>
                </button>
                <button
                  type="button"
                  onClick={() => void pedirExclusao(c)}
                  aria-label={`Apagar a conversa "${c.titulo}"`}
                  className="mr-1 rounded p-1.5 text-conteudo-sutil opacity-0 transition hover:text-erro focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <IconeLixeira />
                </button>
              </div>
            ))}
          </nav>
        </aside>

        {/* ── A conversa ── */}
        <section
          aria-label="Conversa com a BECA"
          className="flex min-w-0 flex-col overflow-hidden rounded-painel border border-borda bg-superficie-elevada shadow-cartao"
        >
          <div
            ref={rolagem}
            role="log"
            aria-live="polite"
            className="flex max-h-[calc(100vh-20rem)] min-h-[22rem] flex-col gap-5 overflow-y-auto px-4 py-6 sm:px-6"
          >
            {conversa.isError && (
              <ErroDaApi titulo="Não foi possível abrir a conversa" erro={conversa.error} />
            )}
            {conversaId !== null && conversa.isPending && (
              <p className="py-10 text-center text-sm text-conteudo-sutil">
                Carregando a conversa…
              </p>
            )}

            {vazia && pendente === null && <BoasVindas ligada={ligada} aoEscolher={enviar} />}

            {mensagens.map((m) =>
              m.autor === 'voce' ? (
                <FalaSua key={m.id} texto={m.texto} />
              ) : (
                <FalaDaBeca key={m.id} mensagem={m} conversaId={conversaId} />
              ),
            )}

            {pendente !== null && (
              <>
                <FalaSua texto={pendente} />
                <Pensando />
              </>
            )}

            {perguntar.isError && (
              <ErroDaApi titulo="A BECA não respondeu" erro={perguntar.error} />
            )}
          </div>

          <form
            onSubmit={aoEnviar}
            className="border-t border-borda bg-superficie-base/60 p-3 sm:p-4"
          >
            <div className="flex items-end gap-2">
              <label htmlFor="pergunta-beca" className="sr-only">
                Pergunta para a BECA
              </label>
              <textarea
                id="pergunta-beca"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={aoTeclar}
                rows={Math.min(6, Math.max(2, texto.split('\n').length))}
                maxLength={4000}
                disabled={!ligada || perguntar.isPending}
                placeholder={
                  ligada
                    ? 'Pergunte à BECA… (Enter envia, Shift+Enter quebra a linha)'
                    : 'A BECA ainda não está ligada.'
                }
                className="min-h-[3rem] flex-1 resize-none rounded-card border border-borda bg-superficie-elevada px-3 py-2.5 text-sm text-conteudo-primario placeholder:text-conteudo-sutil focus:border-acento-indigo focus:outline-none focus:ring-2 focus:ring-acento-indigo/30 disabled:cursor-not-allowed disabled:opacity-60"
              />
              <Botao
                type="submit"
                carregando={perguntar.isPending}
                rotuloDeCarregamento="A BECA está respondendo"
                disabled={!ligada || texto.trim() === ''}
              >
                Enviar
              </Botao>
            </div>
            <p className="mt-2 text-xs text-conteudo-sutil">
              A BECA consulta o Deca e pode errar: confira número importante antes de usar fora
              daqui.
            </p>
          </form>
        </section>
      </div>
    </Pagina>
  );
}

function UsoDoMes(props: { readonly situacao: SituacaoDaBeca | undefined }): JSX.Element | null {
  const s = props.situacao;
  if (s === undefined || !s.ligada) return null;
  const dolar = (v: number): string =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' });
  return (
    <p className="text-right text-xs text-conteudo-secundario">
      Este mês: {s.mes.perguntas.toLocaleString('pt-BR')}{' '}
      {s.mes.perguntas === 1 ? 'pergunta' : 'perguntas'} · {dolar(s.mes.custoUsd)}
      {s.limiteMensalUsd !== null && ` de ${dolar(s.limiteMensalUsd)}`}
    </p>
  );
}

function BoasVindas(props: {
  readonly ligada: boolean;
  readonly aoEscolher: (pergunta: string) => void;
}): JSX.Element {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-4 py-8 text-center">
      <Avatar grande />
      <div className="flex flex-col gap-1">
        <h2 className="font-display text-lg font-semibold text-conteudo-primario">
          Oi! Eu sou a BECA, sua assessora no Deca.
        </h2>
        <p className="text-sm text-conteudo-secundario">
          Pergunte sobre qualquer cliente, negociação, contrato, parcela, o CRM ou a prospecção.
          Peça também para criar ou mover card, registrar atividade, fechar negociação, dar baixa em
          parcela ou mandar cobrança: eu preparo, e só acontece quando você confirmar.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {SUGESTOES.map((s) => (
          <button
            key={s}
            type="button"
            disabled={!props.ligada}
            onClick={() => props.aoEscolher(s)}
            className="rounded-full border border-borda bg-superficie-base px-3 py-1.5 text-sm text-conteudo-secundario transition hover:border-acento-indigo hover:text-conteudo-primario disabled:cursor-not-allowed disabled:opacity-60"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function FalaSua(props: { readonly texto: string }): JSX.Element {
  return (
    <div className="flex justify-end">
      <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-acento-indigo px-4 py-2.5 text-sm text-white">
        {props.texto}
      </p>
    </div>
  );
}

function FalaDaBeca(props: {
  readonly mensagem: MensagemDaBeca;
  readonly conversaId: string | null;
}): JSX.Element {
  const m = props.mensagem;
  return (
    <div className="flex items-start gap-3">
      <Avatar />
      <div className="min-w-0 max-w-[92%] rounded-2xl rounded-tl-md border border-borda bg-superficie-base px-4 py-3">
        <TextoDaBeca texto={m.texto} />
        <AcoesDaResposta acoes={m.acoes} conversaId={props.conversaId} />
        {m.consultas.length > 0 && (
          <p className="mt-3 border-t border-borda pt-2 text-xs text-conteudo-sutil">
            Consultou: {m.consultas.join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}

function Pensando(): JSX.Element {
  return (
    <div className="flex items-start gap-3" aria-live="polite">
      <Avatar />
      <div className="flex items-center gap-3 rounded-2xl rounded-tl-md border border-borda bg-superficie-base px-4 py-3">
        <span className="flex gap-1" aria-hidden="true">
          {[0, 150, 300].map((atraso) => (
            <span
              key={atraso}
              className="h-1.5 w-1.5 rounded-full bg-acento-indigo motion-safe:animate-bounce"
              style={{ animationDelay: `${atraso}ms` }}
            />
          ))}
        </span>
        <span className="text-sm text-conteudo-secundario">Consultando o Deca…</span>
      </div>
    </div>
  );
}

function Avatar(props: { readonly grande?: boolean }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-acento-indigo to-acento-ciano font-display font-bold text-white ${
        props.grande === true ? 'h-14 w-14 text-xl' : 'h-8 w-8 text-sm'
      }`}
    >
      B
    </span>
  );
}

/** "hoje, 14:32", "ontem", "12/10". */
function quando(iso: string): string {
  const data = new Date(iso);
  const hoje = new Date();
  const mesmoDia = (a: Date, b: Date): boolean => a.toDateString() === b.toDateString();
  if (mesmoDia(data, hoje)) {
    return `hoje, ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  }
  const ontem = new Date(hoje);
  ontem.setDate(hoje.getDate() - 1);
  if (mesmoDia(data, ontem)) return 'ontem';
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function IconeMais(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function IconeLixeira(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
    </svg>
  );
}
